import { useEffect, useRef } from 'react';
import type { LoginVerifyContactResponse } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/AccessRpc';
import { useLoginVerify } from './useLoginVerify';
import {
  redirectToAccessDenied,
  redirectToLogin,
} from '../utils/loginRedirect';
import {
  isAccessDeniedError,
  isPortalInstanceAccessDeniedError,
} from '../utils/portalAuthErrors';

export type UsePortalsAuthOptions = {
  redirectUrl?: string;
};

export type UsePortalsAuthResult = {
  isLoading: boolean;
  isAccessDenied: boolean;
  user: LoginVerifyContactResponse | undefined;
};

export function usePortalsAuth(
  options?: UsePortalsAuthOptions
): UsePortalsAuthResult {
  const { data: user, loading, error } = useLoginVerify();

  const isDeniedByResponse =
    !loading && !!user && user.requestedInstanceAccess === 'DENIED';
  const isDeniedByError = !loading && !user && isAccessDeniedError(error);
  const hasNoPortalAccess =
    !loading && !user && isPortalInstanceAccessDeniedError(error);
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (loading || user || isDeniedByError || hasRedirectedRef.current) {
      return;
    }
    hasRedirectedRef.current = true;
    if (hasNoPortalAccess) {
      redirectToAccessDenied();
    } else {
      redirectToLogin(options?.redirectUrl);
    }
  }, [loading, user, isDeniedByError, hasNoPortalAccess, options?.redirectUrl]);

  return {
    isLoading: loading || hasNoPortalAccess,
    isAccessDenied: isDeniedByResponse || isDeniedByError,
    user,
  };
}
